<template>
  <ValidationObserver v-slot="{ passes }">
    <b-modal id="new-customer-modal" size="xl" ref="newCustomerModal" @show="onShow" @hide="onHide">
      <template v-slot:modal-header="{ close }">
        <b-container>
          <div class="modal-title">
            <h3 class="text-bold text-center">{{ isEdit ? 'Edit' : 'New' }} Customer</h3>
            <button type="button" aria-label="Close" class="close" @click="close">
              <i class="di-remove-10"></i>
            </button>
          </div>
        </b-container>
      </template>
      <div class="container">
        <ul class="text-danger" v-if="errors.length">
          <li v-for="(error, index) in errors" v-bind:key="index">{{ error }}</li>
        </ul>
        <h5 class="mb-4">Customer Informations</h5>
        <b-form ref="newCustomerForm">
          <section>
            <h6 class="text-uppercase mb-3">Personal Details</h6>
            <b-row>
              <b-col cols="6">
                <ValidationProvider
                  vid="first-name"
                  name="First Name"
                  mode="lazy"
                  rules="required|no_forbidden_name|valid_name"
                  v-slot="validationContext"
                >
                  <b-form-group label="First Name" label-for="first-name">
                    <b-form-input
                      id="first-name"
                      v-model="form.first_name"
                      :state="getValidationState(validationContext)"
                      type="text"
                      placeholder="i.e. John"
                      autofocus
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col cols="6">
                <ValidationProvider
                  vid="last-name"
                  name="Last Name"
                  mode="lazy"
                  rules="no_forbidden_name|valid_name"
                  v-slot="validationContext"
                >
                  <b-form-group label="Last Name" label-for="last-name">
                    <b-form-input
                      id="last-name"
                      v-model="form.last_name"
                      :state="getValidationState(validationContext)"
                      type="text"
                      placeholder="i.e. Doe"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
          </section>
          <div class="divider mt-1 mb-4"></div>
          <section>
            <h6 class="text-uppercase mb-3">Contact Details</h6>
            <b-row>
              <b-col cols="6">
                <ValidationProvider
                  vid="email"
                  name="Email"
                  mode="lazy"
                  rules="email|no_forbidden_email"
                  v-slot="validationContext"
                >
                  <b-form-group label="Email" label-for="email">
                    <b-form-input
                      v-model="form.email"
                      :state="getValidationState(validationContext)"
                      type="email"
                      placeholder="i.e. johndoe@acmemail.com"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col cols="6">
                <b-form-group
                  label="Marketing Opt-in"
                  label-for="marketing-opt-in"
                  class="flex-fill"
                >
                  <b-form-radio-group
                    id="marketing-opt-in"
                    v-model="form.new_opt_in"
                    :options="optIns"
                    @change="listenChangeNewOptIn"
                    buttons
                    button-variant="white"
                    class="custom-radio-button"
                  />
                </b-form-group>

                <b-form-group label="Channels" label-for="marketing-opt-in" class="flex-fill">
                  <b-form-checkbox
                    v-model="form.channel_sms"
                    class="custom-checkbox-button"
                    name="check-button"
                    inline
                  >
                    Sms
                  </b-form-checkbox>

                  <b-form-checkbox
                    v-model="form.channel_phone"
                    class="custom-checkbox-button"
                    name="check-button"
                    inline
                  >
                    Phone
                  </b-form-checkbox>

                  <b-form-checkbox
                    v-model="form.channel_email"
                    class="custom-checkbox-button"
                    name="check-button"
                    inline
                  >
                    Email
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6">
                <b-form-group label="Customer Feedback" label-for="customer-feedback">
                  <b-form-checkbox
                    id="customer-feedback"
                    v-model="form.customer_feedback_enabled"
                    class="custom-checkbox-button"
                    name="customer-feedback"
                    inline
                    title="Allow us to request feedback about the customer's experience."
                  >
                    Allow feedback requests
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
              <b-col cols="6" v-if="!form.customer_feedback_enabled">
                <ValidationProvider
                  vid="customer-feedback-reason"
                  name="Reason"
                  rules="required"
                  v-slot="validationContext"
                >
                  <b-form-group label="Reason for disabling" label-for="customer-feedback-reason">
                    <b-form-textarea
                      id="customer-feedback-reason"
                      v-model="form.customer_feedback_reason"
                      :state="getValidationState(validationContext)"
                      rows="2"
                      maxlength="500"
                      placeholder="i.e. Customer asked not to receive review invitations"
                    />
                    <b-form-invalid-feedback>
                      Please provide a reason for disabling Customer Feedback.
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6">
                <b-form-group label="Mobile phone" label-for="mobile">
                  <CustomTelInput name="mobile" v-model="form.mobile" />
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="Phone" label-for="phone">
                  <CustomTelInput name="phone" v-model="form.phone" />
                </b-form-group>
              </b-col>
            </b-row>
          </section>
          <div class="divider mt-1 mb-4"></div>
          <section>
            <h6 class="text-uppercase mb-3">Others</h6>
            <b-row>
              <b-col cols="6">
                <b-form-group label="Occupation" label-for="occupation">
                  <b-form-input
                    id="occupation"
                    v-model="form.occupation"
                    type="text"
                    placeholder="i.e. Teacher"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="Hobbies" label-for="hobbies">
                  <b-form-input
                    id="hobbies"
                    v-model="form.hobbies"
                    type="text"
                    placeholder="i.e. Cycling, reading, hiking"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6">
                <b-form-group label="Date of birth" label-for="dob">
                  <b-form-input id="dob" name="dob" v-model="form.dob" type="date"></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="Gender" label-for="gender">
                  <b-form-radio-group
                    id="gender"
                    v-model="form.gender"
                    :options="genders"
                    buttons
                    button-variant="white"
                    class="custom-radio-button"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="postcodeCountry">
              <b-col cols="6">
                <b-form-group label="Postcode" label-for="postcode">
                  <PostcodeInput
                    :country="postcodeCountry"
                    v-model="form.postcode"
                    :state="postcodeState"
                    input-id="postcode"
                  />
                  <b-form-invalid-feedback :state="postcodeState">
                    {{ postcodeError }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
          </section>
        </b-form>
      </div>
      <template v-slot:modal-footer="{ cancel }">
        <div class="flex-fill m-0">
          <div class="container">
            <div class="d-flex justify-content-end">
              <b-button variant="outline-light" @click="cancel()">Cancel</b-button>
              <b-button
                variant="primary"
                @click="passes(submit)"
                class="ml-3"
                :disabled="isLoading"
              >
                <b-spinner v-if="isLoading" small />
                <span v-else>Confirm</span>
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <b-modal
      id="postcode-prompt-modal"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      modal-class="postcode-prompt"
    >
      <div class="postcode-prompt__icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"
            fill="currentColor"
          />
        </svg>
      </div>
      <h5 class="postcode-prompt__title">Customer Postcode</h5>
      <p class="postcode-prompt__text">
        Would you like to add the customer's postcode? This helps us better understand where our
        customers come from.
      </p>
      <b-form-group
        v-if="postcodeCountry"
        class="postcode-prompt__field"
        label="Postcode"
        label-for="prompt-postcode"
      >
        <PostcodeInput
          :country="postcodeCountry"
          v-model="form.postcode"
          :state="postcodeState"
          input-id="prompt-postcode"
        />
        <b-form-invalid-feedback :state="postcodeState">
          {{ postcodeError }}
        </b-form-invalid-feedback>
      </b-form-group>
      <p v-if="postcodeWarning" class="postcode-prompt__warning">{{ postcodeWarning }}</p>
      <div class="postcode-prompt__actions">
        <b-button variant="outline-light" @click="skipPostcodePrompt">Skip</b-button>
        <b-button variant="primary" @click="savePostcodePrompt">Save Postcode</b-button>
      </div>
    </b-modal>
  </ValidationObserver>
</template>

<script>
/* eslint-disable */
import { mapState } from 'vuex';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import apiCustomers from '../api/customers';
import CustomTelInput from './CustomTelInput.vue';
import PostcodeInput from './PostcodeInput.vue';
import { postcodeCountry, isValidPostcode, normalizePostcode } from '../support/postcode';

export default {
  name: 'NewCustomerModal',
  components: {
    ValidationProvider,
    ValidationObserver,
    CustomTelInput,
    PostcodeInput,
  },
  props: ['customer'],
  data() {
    return {
      genders: ['Male', 'Female'],
      optIns: [
        { text: 'All Offers, News and Appointment Reminders', value: 'ALL' },
        { text: 'Only Appointment Reminders', value: 'HEALTH' },
        { text: 'None', value: 'NONE' },
      ],
      form: {
        first_name: '',
        last_name: '',
        gender: '',
        dob: '',
        email: '',
        new_opt_in: 'ALL',
        mobile: '',
        phone: '',
        occupation: '',
        hobbies: '',
        postcode: '',
        channel_phone: true,
        channel_sms: true,
        channel_email: true,
        customer_feedback_enabled: true,
        customer_feedback_reason: '',
      },
      errors: [],
      isLoading: false,
      isPopulating: false,
      postcodeWarning: '',
    };
  },
  computed: {
    ...mapState('customer', ['selectedCustomer']),
    ...mapState('auth', {
      currentUser: state => state.user,
      selectedRegionId: state => state.selectedRegionId,
    }),
    isEdit() {
      return !!this.selectedCustomer;
    },
    postcodeCountry() {
      const regionId =
        (this.isEdit && this.selectedCustomer.region_id) || this.selectedRegionId;
      const region = this.$store.state.regions.find(r => r.id === regionId);
      return postcodeCountry(region && region.country_code);
    },
    postcodeState() {
      if (!this.postcodeCountry || !this.form.postcode) return null;
      return isValidPostcode(this.form.postcode, this.postcodeCountry);
    },
    postcodeError() {
      return this.postcodeCountry === 'CA'
        ? 'Please enter a valid Canadian postal code (e.g. M5V 3L9).'
        : 'Please enter a valid 4-digit Australian postcode.';
    },
  },
  watch: {
    'form.postcode'(val) {
      if (val) this.postcodeWarning = '';
    },
  },
  methods: {
    onShow() {
      if (this.isEdit) {
        this.isPopulating = true;
        this.populateForm(this.selectedCustomer);
        this.$nextTick(() => {
          this.isPopulating = false;
        });
      }
    },
    onHide() {
      this.isLoading = false;
      this.form = this.$options.data.apply(this).form;
      this.errors = [];
      this.postcodeWarning = '';
    },
    populateForm(customer) {
      this.form = Object.keys(this.form).reduce((obj, field) => {
        const val = customer[field];

        if (field === 'dob' && val) {
          Object.assign(obj, { [field]: this.$moment(val).format('YYYY-MM-DD') });
        } else {
          Object.assign(obj, { [field]: val });
        }

        if (field.includes('channel') && val) {
          Object.assign(obj, { [field]: true });
        }

        // Customers created before DP-3694 have no stored value, which means enabled
        if (field === 'customer_feedback_enabled') {
          Object.assign(obj, { [field]: val === null || val === undefined ? true : !!Number(val) });
        }
        if (field === 'customer_feedback_reason') {
          Object.assign(obj, { [field]: val || '' });
        }

        return obj;
      }, {});
    },
    submit() {
      if (this.postcodeCountry) {
        if (this.form.postcode) {
          if (!isValidPostcode(this.form.postcode, this.postcodeCountry)) {
            return;
          }
          this.doSave();
          return;
        }
        this.postcodeWarning = '';
        this.$bvModal.show('postcode-prompt-modal');
        return;
      }
      this.doSave();
    },
    savePostcodePrompt() {
      if (!this.form.postcode) {
        this.postcodeWarning = 'Please enter a postcode, or choose Skip to continue without one.';
        return;
      }
      if (!isValidPostcode(this.form.postcode, this.postcodeCountry)) {
        return;
      }
      this.postcodeWarning = '';
      this.$bvModal.hide('postcode-prompt-modal');
      this.doSave();
    },
    skipPostcodePrompt() {
      this.$bvModal.hide('postcode-prompt-modal');
      this.doSave();
    },
    async doSave() {
      this.isLoading = true;
      if (this.postcodeCountry && this.form.postcode) {
        this.form.postcode = normalizePostcode(this.form.postcode, this.postcodeCountry) || '';
      }
      const payload = {
        ...this.form,
        customer_feedback_enabled: this.form.customer_feedback_enabled ? 1 : 0,
        customer_feedback_reason: this.form.customer_feedback_enabled
          ? null
          : (this.form.customer_feedback_reason || '').trim(),
      };
      try {
        if (this.isEdit) {
          await apiCustomers.updateCustomer(this.selectedCustomer.id, payload);
          this.$store.dispatch('showSuccessAlert', 'Customer has been successfully updated!');
        } else {
          await apiCustomers.postCustomer({
            region_id: this.currentUser.outlet.region_id,
            outlet_id: this.currentUser.outlet.id,
            home_region: this.selectedRegionId,
            active_region: this.selectedRegionId,
            ...payload,
          });
          this.$store.dispatch('showSuccessAlert', 'New customer has been successfully created!');
        }
        // On edit the list patches this row in place instead of reloading, so staff
        // keep their position and the open profile. full_name is first + last on the API.
        const updatedCustomer = this.isEdit
          ? {
              ...payload,
              id: this.selectedCustomer.id,
              full_name: `${payload.first_name} ${payload.last_name}`,
            }
          : null;

        this.$nextTick(() => {
          this.$refs.newCustomerModal.hide('ok');
          if (updatedCustomer) {
            this.$emit('customer-updated', updatedCustomer);
          } else {
            this.$emit('customer-added');
          }
        });
      } catch (error) {
        this.isLoading = false;
        const data = (error.response && error.response.data) || {};
        // api-v4 returns { errors: { field: [...] } }, the legacy api returns { error: [...] }
        this.errors =
          data.error ||
          (data.errors ? Object.values(data.errors).flat() : null) ||
          (data.message ? [data.message] : ['Customer could not be saved.']);
      }
    },
    updateNewOptIn() {
      if (this.isPopulating) {
        return;
      }
      if (
        this.form.channel_sms &&
        this.form.channel_phone &&
        this.form.channel_email &&
        this.form.new_opt_in !== 'HEALTH'
      ) {
        this.form.new_opt_in = 'ALL';
      } else if (!this.form.channel_sms && !this.form.channel_phone && !this.form.channel_email) {
        this.form.new_opt_in = 'NONE';
      }
    },
    listenChangeNewOptIn() {
      if (this.form.new_opt_in === 'NONE') {
        this.form.channel_sms = false;
        this.form.channel_phone = false;
        this.form.channel_email = false;
      } else if (this.form.new_opt_in === 'ALL' || this.form.new_opt_in === 'HEALTH') {
        this.form.channel_sms = true;
        this.form.channel_phone = true;
        this.form.channel_email = true;
      }
    },
  },
  watch: {
    'form.channel_sms': function (newValue) {
      this.updateNewOptIn();
    },
    'form.channel_phone': function (newValue) {
      this.updateNewOptIn();
    },
    'form.channel_email': function (newValue) {
      this.updateNewOptIn();
    },
  },
};
</script>

<style>
.postcode-prompt .modal-dialog {
  max-width: 420px;
}
.postcode-prompt .modal-content {
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}
.postcode-prompt .modal-body {
  padding: 32px;
}
.postcode-prompt__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 20px;
  border-radius: 14px;
  background: #eaf1ff;
  color: #2d7ff9;
}
.postcode-prompt__title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
  color: #1a2233;
}
.postcode-prompt__text {
  margin: 0 0 24px;
  font-size: 14px;
  line-height: 1.55;
  color: #6b7280;
}
.postcode-prompt__field {
  margin-bottom: 0;
}
.postcode-prompt__field > label {
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}
.postcode-prompt .postcode-input .form-control {
  height: 46px;
  border-radius: 10px;
  padding: 0 14px;
  font-size: 15px;
}
.postcode-prompt__warning {
  margin: 10px 0 0;
  font-size: 13px;
  color: #dc3545;
}
.postcode-prompt__actions {
  display: flex;
  gap: 12px;
  margin-top: 28px;
}
.postcode-prompt__actions .btn {
  flex: 1;
  height: 46px;
  border-radius: 10px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
